import React, { useEffect, useState } from "react";
import { addAllowedDomain, listAllowedDomains, removeAllowedDomain } from "../services/settingsService";

function normalizeDomain(value) {
  return value.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/$/, "");
}

function isValidDomain(value) {
  if (!value || value.length > 253 || value.includes("/") || value.includes(":")) return false;
  return /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i.test(value);
}

export default function AllowedDomains() {
  const [domains, setDomains] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  // The allow-list is stored per workspace on the server; the widget start call enforces it.
  useEffect(() => {
    let active = true;
    listAllowedDomains()
      .then((items) => { if (active) setDomains(items); })
      .catch((err) => { if (active) setError(err.message || "Unable to load allowed domains."); })
      .finally(() => { if (active) setLoadingList(false); });
    return () => { active = false; };
  }, []);

  const add = async (event) => {
    event.preventDefault();
    const domain = normalizeDomain(draft);
    setError(""); setNotice("");
    if (!isValidDomain(domain)) { setError("Enter a valid hostname, for example app.yourcompany.com."); return; }
    if (domains.some((item) => item.domain === domain)) { setError("This domain is already on the allow-list."); return; }
    setLoading(true);
    try {
      const saved = await addAllowedDomain(domain);
      setDomains((current) => [...current, saved].sort((a, b) => a.domain.localeCompare(b.domain)));
      setDraft(""); setNotice(`${saved.domain} added to the allow-list.`);
    } catch (err) { setError(err.details?.fields?.domain || err.message || "Unable to add domain."); }
    finally { setLoading(false); }
  };

  const remove = async (item) => {
    setError(""); setNotice(""); setRemoving(item.id);
    try {
      await removeAllowedDomain(item.id);
      setDomains((current) => current.filter((entry) => entry.id !== item.id));
      setNotice(`${item.domain} removed from the allow-list.`);
    } catch (err) { setError(err.message || "Unable to remove domain."); }
    finally { setRemoving(""); }
  };

  return <div className="card p-4">
    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
      <div><h2 className="h5 mb-1">Allowed-domain management</h2><p className="text-muted mb-0">Control which website hostnames may load your ChatBizz widget.</p></div>
      <span className="badge text-bg-warning">Enforced by the widget start call</span>
    </div>
    <form className="row g-2 my-3" onSubmit={add} noValidate>
      <div className="col"><label className="visually-hidden" htmlFor="allowedDomain">Domain name</label><input id="allowedDomain" className={`form-control ${error ? "is-invalid" : ""}`} value={draft} onChange={(event) => { setDraft(event.target.value); setError(""); }} placeholder="app.yourcompany.com" disabled={loading}/></div>
      <div className="col-auto"><button className="btn btn-dark" type="submit" disabled={loading}>{loading ? "Adding…" : "Add domain"}</button></div>
    </form>
    {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}
    {notice && <div className="alert alert-success py-2" role="status">{notice}</div>}
    {loadingList ? <div className="text-center text-muted py-4" role="status">Loading allowed domains…</div>
      : domains.length ? <div className="table-responsive"><table className="table align-middle mb-0"><thead><tr><th>Allowed hostname</th><th className="text-end">Action</th></tr></thead><tbody>{domains.map((item) => <tr key={item.id}><td><i className="bi bi-globe2 text-muted me-2"/>{item.domain}</td><td className="text-end"><button type="button" className="btn btn-sm btn-outline-danger" onClick={() => remove(item)} disabled={Boolean(removing)}>{removing === item.id ? "Removing…" : "Remove"}</button></td></tr>)}</tbody></table></div>
      : <div className="text-center text-muted py-4">No allowed domains. Add a hostname to continue.</div>}
    <p className="small text-muted mt-3 mb-0">A visitor's page origin that is not on this list is rejected when the widget starts (403 DOMAIN_NOT_ALLOWED). The Python backend must enforce the same rule server-side.</p>
  </div>;
}
